import { useState, useRef, useEffect } from "react";
import {
  FileCheck, Upload, X, ArrowRight, Home,
  ShieldCheck, ShieldAlert, ShieldX, ShieldQuestion, Loader2,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { StatusBadge } from "@/components/StatusBadge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useNavigate } from "react-router-dom";
import { useDocuments, useUploadDocument } from "@/hooks/useDocuments";
import { useHosts, useVerifyRegistrationDoc } from "@/hooks/useHosts";
import { useAuth } from "@/contexts/AuthContext";
import { isValidBCRegistrationFormat } from "@/lib/bcRegistry";
import type { Host } from "@/types/database";

// ─── Registry status badge ────────────────────────────────────────────────────

function RegistryBadge({ status }: { status: Host["registry_status"] }) {
  switch (status) {
    case "active":
      return (
        <Badge className="gap-1 bg-green-600 hover:bg-green-600 text-white">
          <ShieldCheck className="h-3 w-3" /> Active
        </Badge>
      );
    case "expired":
      return (
        <Badge className="gap-1 bg-amber-500 hover:bg-amber-500 text-white">
          <ShieldAlert className="h-3 w-3" /> Expired
        </Badge>
      );
    case "suspended":
      return (
        <Badge variant="destructive" className="gap-1">
          <ShieldX className="h-3 w-3" /> Suspended
        </Badge>
      );
    case "not_found":
      return (
        <Badge variant="destructive" className="gap-1">
          <ShieldX className="h-3 w-3" /> Not Found
        </Badge>
      );
    case "error":
      return (
        <Badge variant="outline" className="gap-1 text-muted-foreground">
          <ShieldQuestion className="h-3 w-3" /> Error
        </Badge>
      );
    case "pending":
    default:
      return (
        <Badge variant="outline" className="gap-1 text-muted-foreground">
          <ShieldQuestion className="h-3 w-3" /> Not validated
        </Badge>
      );
  }
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function HostDocuments() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: hosts = [], isLoading: hostsLoading } = useHosts();
  const myHost = hosts.find((h: any) => h.user_id === user?.id) as Host | undefined;
  const { data: docs = [], isLoading } = useDocuments(myHost?.id);
  const uploadDocument = useUploadDocument();
  const verifyRegistrationDoc = useVerifyRegistrationDoc();

  // Upload dialog state
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", expiresAt: "" });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Certificate upload state (registration card)
  const [certFile, setCertFile] = useState<File | null>(null);
  const certFileRef = useRef<HTMLInputElement>(null);

  // Registration number state — synced from myHost once the async query resolves
  const [regNumber, setRegNumber] = useState("");
  useEffect(() => {
    if (myHost?.registration_number) {
      setRegNumber(myHost.registration_number);
    }
  }, [myHost?.registration_number]);
  const regFormatOk = isValidBCRegistrationFormat(regNumber);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setSelectedFile(file);
    if (file && !form.name) {
      setForm((f) => ({ ...f, name: file.name.replace(/\.[^/.]+$/, "") }));
    }
  };

  const handleUpload = () => {
    if (!selectedFile || !form.name.trim() || !myHost?.id) return;
    uploadDocument.mutate(
      { file: selectedFile, hostId: myHost.id, name: form.name.trim(), expiresAt: form.expiresAt || undefined },
      {
        onSuccess: () => {
          setOpen(false);
          setForm({ name: "", expiresAt: "" });
          setSelectedFile(null);
          if (fileInputRef.current) fileInputRef.current.value = "";
        },
      }
    );
  };

  const handleOpenChange = (v: boolean) => {
    setOpen(v);
    if (!v) {
      setForm({ name: "", expiresAt: "" });
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDocVerify = () => {
    if (!myHost?.id || !regFormatOk || !certFile) return;
    verifyRegistrationDoc.mutate(
      { hostId: myHost.id, registrationNumber: regNumber.trim().toUpperCase(), certFile },
      {
        onSuccess: () => {
          setRegNumber(regNumber.trim().toUpperCase());
          setCertFile(null);
          if (certFileRef.current) certFileRef.current.value = "";
        },
      }
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">My Documents</h1>
          <p className="text-sm text-muted-foreground">Upload and manage your STR compliance documents</p>
        </div>
        <Button size="sm" onClick={() => setOpen(true)} disabled={!myHost && !hostsLoading}>
          <Upload className="mr-1 h-4 w-4" /> Upload Document
        </Button>
      </div>

      {/* No host profile */}
      {!hostsLoading && !myHost && (
        <Card className="border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/30">
          <CardContent className="pt-6 pb-6">
            <div className="flex flex-col items-center justify-center text-center space-y-3">
              <Home className="h-10 w-10 text-amber-500 mb-1" />
              <div>
                <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">
                  Add your first property to get started
                </p>
                <p className="text-xs text-amber-700 dark:text-amber-400 mt-1 max-w-sm mx-auto">
                  You need to register a property before uploading compliance documents.
                </p>
              </div>
              <Button size="sm" className="mt-2" onClick={() => navigate("/units")}>
                Add My Property <ArrowRight className="ml-1 h-3.5 w-3.5" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── BC STR Registration Number ─────────────────────────────────────── */}
      {myHost && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base">BC STR Registration Number</CardTitle>
                <CardDescription className="text-xs mt-0.5">
                  Enter your provincial registration number and verify it against the BC STR Registry.
                </CardDescription>
              </div>
              <RegistryBadge status={myHost.registry_status ?? null} />
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* Registration number */}
            <div className="space-y-1.5">
              <Label htmlFor="reg_number" className="text-xs">Registration Number</Label>
              <Input
                id="reg_number"
                placeholder="e.g. H123456789"
                value={regNumber}
                onChange={(e) => setRegNumber(e.target.value.toUpperCase())}
                className={
                  regNumber && !regFormatOk
                    ? "border-destructive focus-visible:ring-destructive"
                    : ""
                }
              />
              {regNumber && !regFormatOk && (
                <p className="text-xs text-destructive">
                  Must be H, PM, or ST followed by 9 digits (e.g. H123456789)
                </p>
              )}
            </div>

            {/* Certificate upload */}
            <div className="space-y-1.5">
              <Label className="text-xs">Registration Certificate</Label>
              <div
                className="flex items-center gap-3 rounded-md border border-dashed p-3 cursor-pointer hover:border-primary/50 transition-colors"
                onClick={() => certFileRef.current?.click()}
              >
                <Upload className="h-4 w-4 text-muted-foreground shrink-0" />
                {certFile ? (
                  <div className="flex flex-1 items-center justify-between min-w-0">
                    <span className="text-xs truncate">{certFile.name}</span>
                    <button
                      type="button"
                      aria-label="Remove certificate"
                      className="ml-2 text-muted-foreground hover:text-foreground"
                      onClick={(e) => {
                        e.stopPropagation();
                        setCertFile(null);
                        if (certFileRef.current) certFileRef.current.value = "";
                      }}
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ) : (
                  <span className="text-xs text-muted-foreground">
                    Upload your BC STR certificate (PDF, JPG, or PNG)
                  </span>
                )}
              </div>
              <input
                ref={certFileRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                className="hidden"
                aria-label="Select registration certificate"
                onChange={(e) => setCertFile(e.target.files?.[0] ?? null)}
              />
            </div>

            {/* Verify button */}
            <Button
              onClick={handleDocVerify}
              disabled={!regFormatOk || !certFile || verifyRegistrationDoc.isPending}
              size="sm"
              className="w-full"
            >
              {verifyRegistrationDoc.isPending ? (
                <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> Verifying certificate…</>
              ) : (
                <><ShieldCheck className="mr-1.5 h-3.5 w-3.5" /> Verify with Certificate</>
              )}
            </Button>

            {/* Last verification result */}
            {myHost.registry_validated_at && (
              <div className="rounded-md border bg-muted/40 px-3 py-2.5">
                <p className="text-xs font-medium text-foreground">
                  Last verified:{" "}
                  {new Date(myHost.registry_validated_at).toLocaleDateString("en-CA", {
                    year: "numeric", month: "long", day: "numeric",
                    hour: "2-digit", minute: "2-digit",
                  })}
                </p>
                {myHost.registry_status === "active" && (
                  <p className="text-xs text-green-700 dark:text-green-400 mt-0.5">
                    Certificate verified — registration number matches your document.
                  </p>
                )}
                {myHost.registry_status === "not_found" && (
                  <p className="text-xs text-destructive mt-0.5">
                    Number on document did not match. Please re-enter and upload again.
                  </p>
                )}
              </div>
            )}

            <p className="text-xs text-muted-foreground">
              Our system reads your certificate and automatically checks that the
              registration number matches. Format: <strong>H</strong> (Host),{" "}
              <strong>PM</strong> (Property Manager), or <strong>ST</strong> (Strata Hotel)
              followed by 9 digits.
            </p>
          </CardContent>
        </Card>
      )}

      {/* ── Documents table ───────────────────────────────────────────────── */}
      <Card>
        <CardContent className="pt-6">
          {isLoading ? (
            <div className="space-y-3">
              {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
            </div>
          ) : docs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <FileCheck className="h-10 w-10 text-muted-foreground/50 mb-3" />
              <p className="text-sm font-medium text-muted-foreground">No documents uploaded</p>
              <p className="text-xs text-muted-foreground">
                Upload your insurance, registration certificate, and other required documents.
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">Document</TableHead>
                  <TableHead className="text-xs">File</TableHead>
                  <TableHead className="text-xs">Status</TableHead>
                  <TableHead className="text-xs">Uploaded</TableHead>
                  <TableHead className="text-xs">Expires</TableHead>
                  <TableHead className="text-xs w-[80px]">View</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {docs.map((d: any) => (
                  <TableRow key={d.id}>
                    <TableCell className="text-sm font-medium">{d.name}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{d.file_name}</TableCell>
                    <TableCell><StatusBadge status={d.status || "pending"} /></TableCell>
                    <TableCell className="text-xs">{new Date(d.uploaded_at).toLocaleDateString()}</TableCell>
                    <TableCell className="text-xs">
                      {d.expires_at ? new Date(d.expires_at).toLocaleDateString() : "—"}
                    </TableCell>
                    <TableCell>
                      {d.file_url ? (
                        <a
                          href={d.file_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-primary underline-offset-4 hover:underline"
                        >
                          Open
                        </a>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* ── Upload Document Dialog ────────────────────────────────────────── */}
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Upload Document</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>File *</Label>
              <div
                className="flex items-center gap-3 rounded-md border border-dashed p-4 cursor-pointer hover:border-primary/50 transition-colors"
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="h-5 w-5 text-muted-foreground shrink-0" />
                {selectedFile ? (
                  <div className="flex flex-1 items-center justify-between min-w-0">
                    <span className="text-sm truncate">{selectedFile.name}</span>
                    <button
                      type="button"
                      title="Remove selected file"
                      aria-label="Remove selected file"
                      className="ml-2 text-muted-foreground hover:text-foreground"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedFile(null);
                        if (fileInputRef.current) fileInputRef.current.value = "";
                      }}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <span className="text-sm text-muted-foreground">Click to select a file</span>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                className="hidden"
                aria-label="Select document file"
                title="Select document file"
                onChange={handleFileChange}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="doc_name">Document Name *</Label>
              <Input
                id="doc_name"
                placeholder="e.g. Insurance Certificate"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="expires_at">Expiry Date (optional)</Label>
              <Input
                id="expires_at"
                type="date"
                value={form.expiresAt}
                onChange={(e) => setForm({ ...form, expiresAt: e.target.value })}
              />
            </div>

            {!myHost && (
              <div className="rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-950/30 p-3 space-y-2">
                <p className="text-xs font-medium text-amber-800 dark:text-amber-300">
                  No property registered yet
                </p>
                <p className="text-xs text-amber-700 dark:text-amber-400">
                  You need to add your property before uploading documents.
                </p>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs"
                  onClick={() => { handleOpenChange(false); navigate("/units"); }}
                >
                  Add My Property <ArrowRight className="ml-1 h-3 w-3" />
                </Button>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => handleOpenChange(false)}>Cancel</Button>
            <Button
              onClick={handleUpload}
              disabled={uploadDocument.isPending || !selectedFile || !form.name.trim() || !myHost?.id}
            >
              {uploadDocument.isPending ? "Uploading..." : "Upload"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
